/* @img-migrated */
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, ArrowLeft, Users, Calendar, MessageCircle, Briefcase, CheckCircle2 } from 'lucide-react';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberLoginPage = () => {
  const [mode, setMode] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [applyData, setApplyData] = useState({ name: '', email: '', phone: '', company: '', interest: '', notes: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/portal';

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    return String(detail);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      // Member login always goes to Member Portal / Community
      // Admin access is available via the Admin button inside the portal
      const redirectTo = location.state?.from?.pathname || '/portal';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!applyData.name.trim() || !applyData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/apply`, applyData);
      setApplied(true);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to submit application');
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    { icon: Users, text: 'Connect with 200+ Dominican-American businesses' },
    { icon: Calendar, text: 'Exclusive networking events and workshops' },
    { icon: MessageCircle, text: 'Community forum for members only' },
    { icon: Briefcase, text: 'Business promotion through our channels' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="member-login-page">
      {/* Left side — Warm, social, inviting */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] relative overflow-hidden flex-col">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E63946] via-[#C62D3A] to-[#1D3557]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative flex-1 flex flex-col justify-center px-10 py-12">
          <Link to="/" data-testid="member-login-logo">
            <Img variant="generic"
              src="/brand/logo-color-alt.png"
              alt="DCCF"
              className="h-16 mb-10"
            />
          </Link>
          <h2 className="text-3xl font-bold text-white mb-3 leading-tight">
            Your Business.<br />
            Your Community.<br />
            Your Chamber.
          </h2>
          <p className="text-white/80 text-base mb-10 leading-relaxed">
            Join the Dominican Chamber of Commerce of Florida and connect with a vibrant network of entrepreneurs and business leaders.
          </p>
          <div className="space-y-4">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-center space-x-3"
              >
                <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <b.icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-white/90 text-sm">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="relative px-10 pb-8 text-white/50 text-xs">
          Not a member yet? <Link to="/membership" className="text-white underline">Learn about membership</Link>
        </div>
      </div>

      {/* Right side — Clean login form */}
      <div className="flex-1 bg-white flex items-center justify-center p-6 sm:p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden mb-6 text-center">
            <Link to="/">
              <Img variant="generic"
                src="/brand/logo-color.png"
                alt="DCCF" className="h-14 mx-auto"
              />
            </Link>
          </div>

          <Link to="/" className="inline-flex items-center text-slate-500 hover:text-[#E63946] text-sm mb-6" data-testid="member-back-home">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back to website
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {mode === 'login' ? 'Sign in to your member account' : 'Apply for Membership'}
            </h1>
            <p className="text-slate-500 text-sm">
              {mode === 'login'
                ? 'Welcome back! Access your benefits, events, and community.'
                : 'Submit your application. Our membership team will review it and invite you.'}
            </p>
          </div>

          {/* Toggle */}
          <div className="flex bg-slate-100 rounded-full p-1 mb-6" data-testid="member-auth-toggle">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-medium rounded-full transition-all ${mode === 'login' ? 'bg-[#E63946] text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              data-testid="member-login-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => { setMode('apply'); setApplied(false); }}
              className={`flex-1 py-2 text-sm font-medium rounded-full transition-all ${mode === 'apply' ? 'bg-[#E63946] text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              data-testid="member-apply-tab"
            >
              Apply
            </button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="member-login-form">
              <div>
                <Label htmlFor="m-email" className="text-slate-700 text-sm">Email address</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="m-email" type="email" required
                    value={loginData.email}
                    onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                    className="pl-10 h-11 rounded-full border-slate-200 focus:border-[#E63946] focus:ring-[#E63946]/20"
                    placeholder="you@company.com"
                    data-testid="member-login-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="m-password" className="text-slate-700 text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="m-password" type="password" required
                    value={loginData.password}
                    onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                    className="pl-10 h-11 rounded-full border-slate-200 focus:border-[#E63946] focus:ring-[#E63946]/20"
                    placeholder="Your password"
                    data-testid="member-login-password"
                  />
                </div>
                <div className="text-right mt-1.5">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[#002249] font-medium hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
              <Button
                type="submit" disabled={isLoading}
                className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] rounded-full text-sm font-semibold shadow-sm"
                data-testid="member-login-submit"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-slate-400">or continue with</span></div>
              </div>

              {/* Google Sign-In */}
              <button
                type="button"
                onClick={loginWithGoogle}
                className="w-full h-11 flex items-center justify-center gap-3 border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </button>
            </form>
          ) : mode === 'apply' && !applied ? (
            <form onSubmit={handleApply} className="space-y-3.5" data-testid="member-apply-form">
              <div>
                <Label className="text-slate-700 text-sm">Full name *</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="text" required value={applyData.name} onChange={e => setApplyData({ ...applyData, name: e.target.value })} className="pl-10 h-11 rounded-full border-slate-200 focus:border-[#E63946] focus:ring-[#E63946]/20" placeholder="Maria Rodriguez" data-testid="apply-name" />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Email address *</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="email" required value={applyData.email} onChange={e => setApplyData({ ...applyData, email: e.target.value })} className="pl-10 h-11 rounded-full border-slate-200 focus:border-[#E63946] focus:ring-[#E63946]/20" placeholder="you@company.com" data-testid="apply-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700 text-sm">Phone</Label>
                  <Input type="tel" value={applyData.phone} onChange={e => setApplyData({ ...applyData, phone: e.target.value })} className="h-11 rounded-full border-slate-200 mt-1.5" placeholder="+1 (xxx) xxx" data-testid="apply-phone" />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Company</Label>
                  <Input type="text" value={applyData.company} onChange={e => setApplyData({ ...applyData, company: e.target.value })} className="h-11 rounded-full border-slate-200 mt-1.5" placeholder="Company name" data-testid="apply-company" />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 text-sm">What interests you?</Label>
                <select value={applyData.interest} onChange={e => setApplyData({ ...applyData, interest: e.target.value })} className="w-full h-11 rounded-full border border-slate-200 px-4 text-sm focus:outline-none mt-1.5" data-testid="apply-interest">
                  <option value="">Select...</option>
                  <option value="Networking">Networking</option>
                  <option value="Business Growth">Business Growth</option>
                  <option value="Events">Events & Workshops</option>
                  <option value="Trade">DR-Florida Trade</option>
                  <option value="Community">Community</option>
                  <option value="Visibility">Business Visibility</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Additional notes</Label>
                <textarea value={applyData.notes} onChange={e => setApplyData({ ...applyData, notes: e.target.value })} className="w-full border border-slate-200 rounded-2xl px-4 py-2.5 text-sm focus:outline-none mt-1.5 min-h-[50px] resize-none" placeholder="Tell us about your business..." data-testid="apply-notes" />
              </div>
              <Button type="submit" disabled={isLoading} className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] rounded-full text-sm font-semibold shadow-sm" data-testid="apply-submit">
                {isLoading ? 'Submitting...' : 'Submit Application'}<ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          ) : mode === 'apply' && applied ? (
            <div className="text-center py-6" data-testid="apply-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Application Submitted!</h2>
              <p className="text-sm text-slate-500 max-w-sm mx-auto">Our membership team will review your application and contact you with next steps.</p>
              <p className="text-xs text-slate-400 mt-3">You'll receive an invitation email once approved.</p>
            </div>
          ) : null}

          {/* Footer links */}
          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">
              Are you part of the Chamber team? <Link to="/login" className="text-[#002249] font-medium hover:underline" data-testid="admin-login-link">Staff / Admin Login</Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MemberLoginPage;
